import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Input,
  Badge,
  Box,
  VStack,
} from '@chakra-ui/react';
import { FileText, Send, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import waApi from '../../services/whatsapp/waApi';

interface Template {
  id: string;
  name: string;
  status: string;
  language: string;
  category: string;
  components: Array<{ type: string; text?: string }>;
}

interface SendTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  clientName?: string;
  onSent: () => void;
}

function getBodyText(t: Template): string {
  return t.components.find(c => c.type === 'BODY')?.text || '';
}

function countParams(text: string): number {
  const matches = text.match(/\{\{\d+\}\}/g);
  return matches ? matches.length : 0;
}

export default function SendTemplateModal({
  isOpen,
  onClose,
  conversationId,
  clientName,
  onSent,
}: SendTemplateModalProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Template | null>(null);
  const [params, setParams] = useState<string[]>([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelected(null);
    setParams([]);
    setSearch('');
    setLoading(true);
    waApi
      .get('/wa-templates', { params: { status: 'APPROVED' } })
      .then(res => setTemplates(res.data?.data || []))
      .catch(() => toast.error('Erro ao carregar templates'))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const filtered = templates.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.name.includes(q) || getBodyText(t).toLowerCase().includes(q);
  });

  const handleSelect = (t: Template) => {
    setSelected(t);
    const body = getBodyText(t);
    const count = countParams(body);
    const initial = Array(count).fill('');
    // {{1}} é sempre o nome do cliente
    if (count > 0 && clientName) {
      initial[0] = clientName;
    }
    setParams(initial);
  };

  const handleSend = async () => {
    if (!selected) return;
    if (params.some(p => !p.trim())) {
      toast.error('Preencha todos os parâmetros');
      return;
    }

    setSending(true);
    try {
      await waApi.post('/wa-templates/send', {
        conversation_id: conversationId,
        template_name: selected.name,
        language_code: selected.language,
        parameters: params.length > 0 ? params : undefined,
      });
      toast.success('Template enviado!');
      onSent();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Erro ao enviar template';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const preview = selected
    ? (() => {
        let text = getBodyText(selected);
        params.forEach((p, i) => {
          text = text.replace(`{{${i + 1}}}`, p || `{{${i + 1}}}`);
        });
        return text;
      })()
    : '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">Enviar Template</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!selected ? (
            <>
              <div className="relative mb-3">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Buscar template..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-green-400 focus:outline-none"
                />
              </div>

              {loading && (
                <Text color="gray.400" fontSize="sm" textAlign="center" py={6}>
                  Carregando...
                </Text>
              )}
              {!loading && filtered.length === 0 && (
                <div className="py-8 text-center">
                  <FileText size={32} className="mx-auto mb-2 text-gray-300" />
                  <Text color="gray.400" fontSize="sm">
                    Nenhum template aprovado encontrado
                  </Text>
                </div>
              )}
              {!loading && filtered.length > 0 && (
                <VStack spacing={2} align="stretch" maxH="300px" overflowY="auto">
                  {filtered.map(t => (
                    <Box
                      key={t.id}
                      p={3}
                      borderRadius="lg"
                      border="1px solid"
                      borderColor="gray.200"
                      cursor="pointer"
                      _hover={{ borderColor: 'green.300', bg: 'green.50' }}
                      onClick={() => handleSelect(t)}
                    >
                      <div className="mb-1 flex items-center gap-2">
                        <Text fontSize="sm" fontWeight="600">
                          {t.name}
                        </Text>
                        <Badge fontSize="9px" colorScheme="green">
                          {t.language}
                        </Badge>
                        {countParams(getBodyText(t)) > 0 && (
                          <Badge fontSize="9px" colorScheme="purple">
                            {countParams(getBodyText(t))} params
                          </Badge>
                        )}
                      </div>
                      <Text fontSize="xs" color="gray.500" noOfLines={2}>
                        {getBodyText(t)}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Text fontSize="sm" fontWeight="600">
                  {selected.name}
                </Text>
                <Button size="xs" variant="ghost" onClick={() => setSelected(null)}>
                  Trocar
                </Button>
              </div>

              {params.length > 0 && (
                <div className="space-y-2">
                  <Text fontSize="xs" fontWeight="600" color="gray.500">
                    PARÂMETROS
                  </Text>
                  {params.map((p, i) => (
                    <Input
                      key={`param-${i + 1}`}
                      size="sm"
                      placeholder={`Parâmetro {{${i + 1}}}`}
                      value={p}
                      onChange={e => {
                        const next = [...params];
                        next[i] = e.target.value;
                        setParams(next);
                      }}
                    />
                  ))}
                </div>
              )}

              <Box bg="gray.50" borderRadius="lg" p={3}>
                <Text fontSize="xs" fontWeight="600" color="gray.400" mb={1}>
                  PREVIEW
                </Text>
                <Box bg="white" borderRadius="lg" p={3} shadow="sm" maxW="280px">
                  <Text fontSize="sm" whiteSpace="pre-wrap">
                    {preview}
                  </Text>
                </Box>
              </Box>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>
            Cancelar
          </Button>
          {selected && (
            <Button
              size="sm"
              colorScheme="green"
              leftIcon={<Send size={14} />}
              onClick={handleSend}
              isLoading={sending}
              loadingText="Enviando..."
              isDisabled={params.some(p => !p.trim()) && params.length > 0}
            >
              Enviar Template
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
